import { CareerLevel, CareerLevelDef } from '../types/career';
import { CAREER_BASE_RATES, FIRST_YEAR_COMMISSION_COEFFICIENT, STANDARD_CONTRACT_AE } from './rates';

/**
 * Complete Starlife career levels specifications.
 */
export const CAREER_LEVEL_DEFINITIONS: Record<CareerLevel, CareerLevelDef> = {
  1: {
    level: 1,
    title: 'Консультант',
    minCumulativeUnits: 0,
    minPersonalUnits: 0,
    requiredBranches: [],
    baseRateUAH: CAREER_BASE_RATES[1],
    netRateUAH: CAREER_BASE_RATES[1] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[1] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  2: {
    level: 2,
    title: 'Субагент',
    minCumulativeUnits: 100,
    minPersonalUnits: 0,
    requiredBranches: [],
    baseRateUAH: CAREER_BASE_RATES[2],
    netRateUAH: CAREER_BASE_RATES[2] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[2] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  3: {
    level: 3,
    title: 'Менеджер',
    minCumulativeUnits: 700,
    minPersonalUnits: 300, // Mandatory >= 300 personal AE
    requiredBranches: [],
    baseRateUAH: CAREER_BASE_RATES[3],
    netRateUAH: CAREER_BASE_RATES[3] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[3] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  4: {
    level: 4,
    title: 'Старший менеджер',
    minCumulativeUnits: 3000,
    minPersonalUnits: 300,
    requiredBranches: [2, 2], // At least 2 branches >= Level 2
    baseRateUAH: CAREER_BASE_RATES[4],
    netRateUAH: CAREER_BASE_RATES[4] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[4] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  5: {
    level: 5,
    title: 'Менеджер по управлінню',
    minCumulativeUnits: 10000,
    minPersonalUnits: 300,
    requiredBranches: [3, 3, 2], // At least 2 branches >= Level 3 and 1 branch >= Level 2
    baseRateUAH: CAREER_BASE_RATES[5],
    netRateUAH: CAREER_BASE_RATES[5] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[5] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  6: {
    level: 6,
    title: 'Директор',
    minCumulativeUnits: 25000,
    minPersonalUnits: 300,
    requiredBranches: [4, 4, 3, 2], // 2 x L4, 1 x L3, 1 x L2
    baseRateUAH: CAREER_BASE_RATES[6],
    netRateUAH: CAREER_BASE_RATES[6] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[6] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  7: {
    level: 7,
    title: 'Золотий директор',
    minCumulativeUnits: 60000,
    minPersonalUnits: 300,
    requiredBranches: [5, 5, 4, 3], // 2 x L5, 1 x L4, 1 x L3
    baseRateUAH: CAREER_BASE_RATES[7],
    netRateUAH: CAREER_BASE_RATES[7] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[7] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
  8: {
    level: 8,
    title: 'Президент / Співвласник',
    minCumulativeUnits: 100000,
    minPersonalUnits: 300,
    requiredBranches: [6, 6, 5, 4], // 2 x L6, 1 x L5, 1 x L4
    baseRateUAH: CAREER_BASE_RATES[8],
    netRateUAH: CAREER_BASE_RATES[8] * FIRST_YEAR_COMMISSION_COEFFICIENT,
    payoutPer50AERegularUAH: STANDARD_CONTRACT_AE * CAREER_BASE_RATES[8] * FIRST_YEAR_COMMISSION_COEFFICIENT,
  },
};

/**
 * Get level configuration object.
 */
export function getLevelDefinition(level: CareerLevel): CareerLevelDef {
  const def = CAREER_LEVEL_DEFINITIONS[level];
  if (!def) {
    throw new Error(`Level definition not found for level: ${level}`);
  }
  return def;
}

/**
 * Get all career level definitions sorted by level 1-8.
 */
export function getAllLevelDefinitions(): CareerLevelDef[] {
  return [1, 2, 3, 4, 5, 6, 7, 8].map((lvl) => getLevelDefinition(lvl as CareerLevel));
}

/**
 * Get title in Ukrainian for career level.
 */
export function getLevelTitle(level: CareerLevel): string {
  return getLevelDefinition(level).title;
}

/**
 * Check if the level is Director level (6, 7, 8).
 */
export function isDirectorLevel(level: CareerLevel): boolean {
  return level >= 6;
}

/**
 * Check if the level is Co-owner / President level (8).
 */
export function isCoOwnerLevel(level: CareerLevel): boolean {
  return level === 8;
}
